import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
    THEMES,
    applyTheme,
    getStoredPreference,
    resolveTheme,
    setThemePreference,
    canUseDarkMode,
} from '../utils/theme';

const ThemeContext = createContext({
    preference: THEMES.LIGHT,
    theme: THEMES.LIGHT,
    isDark: false,
    setPreference: () => {},
    setTheme: () => {},
    canUseDarkMode: false,
});

function buildMuiTheme(mode) {
    const isDark = mode === THEMES.DARK;
    return createTheme({
        palette: {
            mode: isDark ? 'dark' : 'light',
            primary: {
                main: '#de2c48',
            },
            secondary: {
                main: '#16b4f3',
            },
            error: {
                main: '#de2c48',
            },
            background: {
                default: isDark ? '#0b1020' : '#fbeaec',
                paper: isDark ? '#171e33' : '#ffffff',
            },
            text: {
                primary: isDark ? '#e8eaef' : '#3c4858',
                secondary: isDark ? '#a0a8bc' : '#666b77',
            },
            divider: isDark ? '#3a4563' : '#e5e6e8',
        },
        typography: {
            fontFamily: 'Livvic, Helvetica, Arial, sans-serif',
            button: {
                textTransform: 'none',
                fontWeight: 600,
            },
        },
        shape: {
            borderRadius: 8,
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: isDark ? '#0b1020' : '#fbeaec',
                        color: isDark ? '#e8eaef' : '#3c4858',
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 600,
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                    },
                },
            },
        },
    });
}

export function AppThemeProvider({ children }) {
    const [preference, setPreferenceState] = useState(() => getStoredPreference());
    const [resolved, setResolvedState] = useState(() => {
        const initialPref = getStoredPreference();
        const initialResolved = resolveTheme(initialPref);
        applyTheme(initialResolved);
        return initialResolved;
    });

    const syncFromStorage = useCallback(() => {
        const nextPref = getStoredPreference();
        const nextResolved = resolveTheme(nextPref);
        setPreferenceState(nextPref);
        setResolvedState(nextResolved);
        applyTheme(nextResolved);
    }, []);

    // Re-resolve when auth session changes (e.g. login as a role without dark mode).
    useEffect(() => {
        syncFromStorage();
        window.addEventListener('storage', syncFromStorage);
        window.addEventListener('spara-theme-auth', syncFromStorage);
        return () => {
            window.removeEventListener('storage', syncFromStorage);
            window.removeEventListener('spara-theme-auth', syncFromStorage);
        };
    }, [syncFromStorage]);

    // Follow OS theme when preference is "system".
    useEffect(() => {
        if (preference !== THEMES.SYSTEM || !canUseDarkMode()) {
            return undefined;
        }
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = () => {
            const nextResolved = resolveTheme(THEMES.SYSTEM);
            setResolvedState(nextResolved);
            applyTheme(nextResolved);
        };
        onChange();
        if (mq.addEventListener) {
            mq.addEventListener('change', onChange);
            return () => mq.removeEventListener('change', onChange);
        }
        mq.addListener(onChange);
        return () => mq.removeListener(onChange);
    }, [preference]);

    useEffect(() => {
        applyTheme(resolved);
    }, [resolved]);

    const setPreference = useCallback((next) => {
        if (!canUseDarkMode() && next !== THEMES.LIGHT) {
            const result = setThemePreference(THEMES.LIGHT);
            setPreferenceState(result.preference);
            setResolvedState(result.resolved);
            return;
        }
        const result = setThemePreference(next);
        setPreferenceState(result.preference);
        setResolvedState(result.resolved);
    }, []);

    const muiTheme = useMemo(() => buildMuiTheme(resolved), [resolved]);

    const value = useMemo(
        () => ({
            preference,
            theme: resolved,
            isDark: resolved === THEMES.DARK,
            setPreference,
            setTheme: setPreference,
            canUseDarkMode: canUseDarkMode(),
        }),
        [preference, resolved, setPreference]
    );

    return (
        <ThemeContext.Provider value={value}>
            <MuiThemeProvider theme={muiTheme}>
                <CssBaseline />
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
}

export function useThemeMode() {
    return useContext(ThemeContext);
}

export default AppThemeProvider;
